'use client';
import { useCallback, useEffect, useState } from 'react';
import cn from '@utils/cn';
import DesignAnimated from './DesignAnimated';
import { DESIGN_SIZE } from './constants';
import DesignWrapper from './DesignWrapper';

const HOVER_DEBOUNCE_MS = 500;

type DesignClientProps = {
  hash: string;
  name?: string;
  url?: string;
  isAnimated?: boolean;
  hideIcon?: boolean;
  designSize?: DESIGN_SIZE;
  className?: string;
  width?: number;
  height?: number;
};

export default function DesignClient({
  hash,
  name,
  url,
  isAnimated,
  hideIcon,
  designSize = DESIGN_SIZE.DEFAULT,
  className,
}: DesignClientProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const onMouseEnter = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setIsHovered(true);
    }, HOVER_DEBOUNCE_MS);
    setTimeoutId(timeoutId);
  }, []);

  const onMouseLeave = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsHovered(false);
  }, [timeoutId]);

  // cleanup running timeouts
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  function renderDesign() {
    return (
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={cn(
          'relative flex w-full items-center justify-center overflow-hidden bg-white',
          'border-border rounded-lg border',
          {
            'min-h-[50px] lg:min-h-[144px]': designSize === DESIGN_SIZE.DEFAULT,
            'min-h-[500px]': designSize === DESIGN_SIZE.HERO,
          },
        )}
      >
        <DesignAnimated
          hash={hash}
          name={name}
          isAnimated={isAnimated}
          hideIcon={hideIcon}
          isPlaying={isHovered}
          designSize={designSize}
        />
      </div>
    );
  }

  return (
    <DesignWrapper url={url} name={name} className={className}>
      {renderDesign()}
    </DesignWrapper>
  );
}
